import { MdOutlineDeleteOutline } from 'react-icons/md'

import { Alert } from '@vtex/shoreline'

import { Button } from '~/components/Buttons'
import { Label } from '~/components/unform'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'

import * as S from './styles'

type OffsiteCreativesListProps = {
  creatives: BannersToTable[]
  // Meta requires a headline, a description and a call to action per
  // creative; DV360 does not (prototype PR #44 declares the extra fields
  // only under `adtech.meta.creatives.*`).
  isMetaChannel: boolean
}

/**
 * Accepted creatives on the offsite briefing flow, as cards instead of the
 * shared table — the layout the prototype defines for this step
 * (ads-design-prototypes, feat/offsite). Every other campaign keeps the
 * table: it holds one editable field per row comfortably, and four it does
 * not (2026-09-15 demo, SPEC 004 items 4.3 and 7.1).
 *
 * Every field is registered against the creative: the destination URL
 * through `handleUpdateDestinationUrl`, exactly as it did in the table, and
 * the Meta copy (headline, description, CTA) through
 * `handleUpdateCreativeCopy`. Sent as structured top-level fields on the
 * asset (`headline`/`description`/`call_to_action`), not merged into
 * `mediaNotes` -- gated by `channel === META` (dto.ts, `mapCreativeCopy`).
 */
const OffsiteCreativesList: React.FC<OffsiteCreativesListProps> = ({
  creatives,
  isMetaChannel
}) => {
  const { t } = useAPPTranslation()

  if (!creatives.length) return null

  return (
    <S.List>
      <S.Header>
        <S.Title>{t('rm:campaign.create.Banner.creatives.title')}</S.Title>
        <S.Count>
          {t('rm:campaign.create.Banner.creatives.count', {
            count: creatives.length
          })}
        </S.Count>
      </S.Header>

      {isMetaChannel && (
        <Alert variant="informational">
          {t('rm:campaign.create.Banner.creatives.metaOnlyNote')}
        </Alert>
      )}

      {creatives.map(creative => {
        return (
          <S.Card key={creative.id}>
            <S.Head>
              <S.Thumb aria-hidden="true">
                {!!creative.media_url && (
                  <img src={creative.media_url} alt="" />
                )}
              </S.Thumb>

              <S.Meta>
                <S.Name
                  title={creative.fileMedia?.name || creative.ad_size_name}
                >
                  {creative.fileMedia?.name || creative.ad_size_name}
                </S.Name>
                <S.Sizes>
                  {creative.dimensions?.width}×{creative.dimensions?.height}
                  {!!creative.raw_dimensions && (
                    <>
                      {' · '}
                      {creative.raw_dimensions.width}×
                      {creative.raw_dimensions.height}
                    </>
                  )}
                </S.Sizes>
              </S.Meta>

              <Button
                onClick={() => creative.handleDeleteBanner(creative)}
                template="transparentCritical"
                size="xsmall"
                iconLeft={<MdOutlineDeleteOutline />}
                text={t('rm:campaign.create.Banner.table.actions.removeBanner')}
              />
            </S.Head>

            <S.Fields>
              <S.Field>
                <label htmlFor={`destination-url-${creative.id}`}>
                  {t('rm:campaign.create.Banner.destinationUrl.label')}
                </label>
                <input
                  id={`destination-url-${creative.id}`}
                  type="text"
                  inputMode="url"
                  className="form-control form-control-sm"
                  placeholder={t(
                    'rm:campaign.create.Banner.destinationUrl.placeholder'
                  )}
                  defaultValue={creative.destination_url}
                  onChange={event =>
                    creative.handleUpdateDestinationUrl?.(
                      creative.id,
                      event.target.value
                    )
                  }
                />
              </S.Field>

              {isMetaChannel && (
                <>
                  <S.Field>
                    <Label htmlFor={`headline-${creative.id}`} isRequired>
                      {t('rm:campaign.create.Banner.creatives.headline')}
                    </Label>
                    <input
                      id={`headline-${creative.id}`}
                      type="text"
                      className="form-control form-control-sm"
                      placeholder={t(
                        'rm:campaign.create.Banner.creatives.headline.placeholder'
                      )}
                      value={creative.headline || ''}
                      onChange={event =>
                        creative.handleUpdateCreativeCopy?.(
                          creative.id,
                          'headline',
                          event.target.value
                        )
                      }
                    />
                  </S.Field>

                  <S.Field>
                    <Label htmlFor={`description-${creative.id}`} isRequired>
                      {t('rm:campaign.create.Banner.creatives.description')}
                    </Label>
                    <input
                      id={`description-${creative.id}`}
                      type="text"
                      className="form-control form-control-sm"
                      placeholder={t(
                        'rm:campaign.create.Banner.creatives.description.placeholder'
                      )}
                      value={creative.description || ''}
                      onChange={event =>
                        creative.handleUpdateCreativeCopy?.(
                          creative.id,
                          'description',
                          event.target.value
                        )
                      }
                    />
                  </S.Field>

                  <S.Field>
                    <Label htmlFor={`cta-${creative.id}`} isRequired>
                      {t('rm:campaign.create.Banner.creatives.cta')}
                    </Label>
                    <input
                      id={`cta-${creative.id}`}
                      type="text"
                      className="form-control form-control-sm"
                      placeholder={t(
                        'rm:campaign.create.Banner.creatives.cta.placeholder'
                      )}
                      value={creative.call_to_action || ''}
                      onChange={event =>
                        creative.handleUpdateCreativeCopy?.(
                          creative.id,
                          'call_to_action',
                          event.target.value
                        )
                      }
                    />
                  </S.Field>
                </>
              )}
            </S.Fields>
          </S.Card>
        )
      })}
    </S.List>
  )
}

export default OffsiteCreativesList
